import { useCallback, useEffect, useReducer, useRef, useState } from 'react';

import { API } from '../../common/EsConstants';
import { esPost, esPut, esRead } from '../../common/EsFetch';
import { useAuth } from '../parent/EsAuth';

/**
 * The game's memory (T6, rolloutEsScreenBar): what an account last had open at a campaign
 * -- the screens, the Actions tab, the codex entry, the sheet, and whatever a later piece
 * names -- kept by esback (EsCampaignMemoryController) so it follows the account between
 * phone and laptop, with a localStorage cache so a reload paints it before esback answers.
 *
 * <p><b>esback is the truth and the cache only a cache</b>, the theme's rule
 * (EsThemeContext): the first paint reads the cache, and esback's answer then wins for every
 * name nothing has set since the page opened. A name set before the answer keeps the
 * press, and nothing is written until the answer is in, so a default can never overwrite a
 * remembered value on the way in.
 *
 * <p><b>One store per account per campaign</b>, shared by every hook that names it, so the
 * console's tab and the screens' pair are one patch stream and never race. Writes go one
 * at a time; what is pressed meanwhile is folded into the next.
 *
 * <p>The API is generic: {@link useEsRemembered} is useState with a name. The Notes screen's
 * open note is `useEsRemembered( campaignId, 'note', null )` and nothing more. A null
 * campaign id is the flag off: plain state, no storage, no request.
 *
 * <p><b>The Commons is a scope too</b> (MEMORY_SCOPE_COMMONS in place of a campaign id): its
 * screen bar's pair, one row per account that esback keeps with no campaign
 * (EsCampaignMemory, campaign_id null), read and patched at commonsMemory by the same rules.
 * Nothing visits it: the Commons is not a game signing in lands on.
 */

export const API_CAMPAIGN_MEMORY = API + 'campaignMemory';
export const API_CAMPAIGN_MEMORY_LAST = API_CAMPAIGN_MEMORY + '/last';
export const API_CAMPAIGN_MEMORY_SESSION = API_CAMPAIGN_MEMORY + '/session';
export const API_COMMONS_MEMORY = API + 'commonsMemory';

export const MEMORY_SCOPE_COMMONS = 'commons';

const CACHE_PREFIX = 'es.gameMemory.';

// Values are small -- a key, an id, a pair -- so JSON is the comparison.
const same = ( pA, pB ) => JSON.stringify( pA ) === JSON.stringify( pB );

// A refusal makes localStorage throw on the read as well as on the write.
const readCache = ( pKey ) => {
    if ( !pKey ) return null;
    try {
        const aParsed = JSON.parse( window.localStorage.getItem( pKey ) );
        return aParsed && typeof aParsed === 'object' && !Array.isArray( aParsed ) ? aParsed : null;
    }
    catch {
        return null;
    }
};

const writeCache = ( pKey, pValues ) => {
    if ( !pKey ) return;
    try {
        window.localStorage.setItem( pKey, JSON.stringify( pValues ) );
    }
    catch {
        // Private mode, a full disk: esback still has it.
    }
};

function createStore( pAccountId, pCampaignId ) {
    // No account id, no cache: one browser's accounts must not read each other's.
    const cacheKey = pAccountId == null ? null : CACHE_PREFIX + pAccountId + '.' + pCampaignId;
    const path = pCampaignId === MEMORY_SCOPE_COMMONS ? API_COMMONS_MEMORY : API_CAMPAIGN_MEMORY + '/' + pCampaignId;
    const listeners = new Set();

    let values = readCache( cacheKey ) ?? {};
    let touched = new Set();
    let pending = {};
    let hydrated = false;
    let inFlight = false;
    let refs = 0;
    // Nothing to send until esback has answered, so a fresh store is not yet saved.
    let saved = false;

    const notify = () => {
        saved = hydrated && !inFlight && Object.keys( pending ).length === 0;
        listeners.forEach( ( pListener ) => pListener() );
    };

    const storeKey = pAccountId + '|' + pCampaignId;
    let sending = {};

    // Nobody holds it and nothing is left to send: the next game opened starts from the
    // cache and asks esback again, so another device's changes come back.
    const drop = () => {
        if ( refs === 0 && saved && stores.get( storeKey ) === store ) stores.delete( storeKey );
    };

    const flush = () => {
        if ( !hydrated || inFlight || Object.keys( pending ).length === 0 ) {
            drop();
            return;
        }
        sending = pending;
        pending = {};
        inFlight = true;
        notify();
        esPut( path, sending )
            // A refusal is not retried: the same patch would be refused again. The cache
            // still holds it for this browser.
            .catch( () => {} )
            .finally( () => {
                sending = {};
                inFlight = false;
                notify();
                flush();
            } );
    };

    // esback's answer over the cache, for every name not pressed since this began and not
    // on its way there -- a read racing a write must not undo it.
    const hydrate = () => {
        touched = new Set( [ ...Object.keys( pending ), ...Object.keys( sending ) ] );
        esRead( path )
            .then( ( pServer ) => {
                const aServer = pServer && typeof pServer === 'object' && !Array.isArray( pServer ) ? pServer : {};
                const aNext = { ...values };
                new Set( [ ...Object.keys( values ), ...Object.keys( aServer ) ] ).forEach( ( aName ) => {
                    if ( touched.has( aName ) ) return;
                    if ( aName in aServer ) aNext[ aName ] = aServer[ aName ];
                    else delete aNext[ aName ];
                } );
                values = aNext;
                writeCache( cacheKey, values );
            } )
            // Unreachable: the cache stands, and the presses still go when they can.
            .catch( () => {} )
            .finally( () => {
                hydrated = true;
                notify();
                flush();
            } );
    };

    const store = {
        has: ( pName ) => Object.prototype.hasOwnProperty.call( values, pName ),
        get: ( pName ) => values[ pName ],
        isSaved: () => saved,
        isHydrated: () => hydrated,

        set( pName, pValue ) {
            const aForget = pValue === null || pValue === undefined;
            if ( aForget ? !( pName in values ) : same( values[ pName ], pValue ) ) return;
            values = { ...values };
            if ( aForget ) delete values[ pName ];
            else values[ pName ] = pValue;
            touched.add( pName );
            pending = { ...pending, [ pName ]: aForget ? null : pValue };
            writeCache( cacheKey, values );
            notify();
            flush();
        },

        subscribe( pListener ) {
            listeners.add( pListener );
            return () => listeners.delete( pListener );
        },

        // Asked each time the game opens. A microtask before the drop, so StrictMode's
        // release-and-retain keeps the store it had.
        retain() {
            refs += 1;
            if ( refs === 1 ) hydrate();
            return () => {
                refs -= 1;
                queueMicrotask( drop );
            };
        },
    };
    return store;
}

const stores = new Map();

const storeFor = ( pAccountId, pCampaignId ) => {
    const aKey = pAccountId + '|' + pCampaignId;
    if ( !stores.has( aKey ) ) stores.set( aKey, createStore( pAccountId, pCampaignId ) );
    return stores.get( aKey );
};

/** The store for this campaign, or null when the campaign is (the flag off) null. */
export function useEsGameMemory( pCampaignId ) {
    const aAccountId = useAuth()?.account?.accountId ?? null;
    const store = pCampaignId == null ? null : storeFor( aAccountId, pCampaignId );

    useEffect( () => store?.retain(), [ store ] );

    return store;
}

/**
 * useState, remembered under pName at this campaign. pInitial is what an account that has
 * remembered nothing sees. The setter takes a value or an updater, as useState's does; null
 * forgets the name.
 */
export function useEsRemembered( pCampaignId, pName, pInitial ) {
    const store = useEsGameMemory( pCampaignId );
    const initial = useRef( pInitial );

    const [ value, setValue ] = useState( () => ( store?.has( pName ) ? store.get( pName ) : pInitial ) );
    const current = useRef( value );

    // The store arriving (the campaign read, the flag answered) and esback answering.
    useEffect( () => {
        if ( !store ) return undefined;
        const sync = () => {
            const aValue = store.has( pName ) ? store.get( pName ) : initial.current;
            if ( same( aValue, current.current ) ) return;
            current.current = aValue;
            setValue( aValue );
        };
        sync();
        return store.subscribe( sync );
    }, [ store, pName ] );

    const remember = useCallback( ( pNext ) => {
        const aValue = typeof pNext === 'function' ? pNext( current.current ) : pNext;
        current.current = aValue;
        setValue( aValue );
        store?.set( pName, aValue );
    }, [ store, pName ] );

    return [ value, remember ];
}

// How long a default waits on an esback that has not answered before it shows anyway.
const READY_WAIT_MS = 3000;

/**
 * Whether esback has answered (or refused) for this campaign, so a default chosen in place
 * of a remembered value is not one esback is about to replace. The flag off is always ready.
 * Only a caller that wants a default (pWanted) listens: one re-render, and none for the rest.
 */
export function useEsGameMemoryReady( pCampaignId, pWanted ) {
    const store = useEsGameMemory( pCampaignId );
    const [ late, setLate ] = useState( false );
    const [ , answered ] = useReducer( ( pCount ) => pCount + 1, 0 );
    const ready = !store || store.isHydrated() || late;

    useEffect( () => {
        if ( !store || !pWanted || ready ) return undefined;
        // Answered between the render and here.
        if ( store.isHydrated() ) {
            answered();
            return undefined;
        }
        const aOff = store.subscribe( () => { if ( store.isHydrated() ) answered(); } );
        const aWait = setTimeout( () => setLate( true ), READY_WAIT_MS );
        return () => {
            clearTimeout( aWait );
            aOff();
        };
    }, [ store, pWanted, ready ] );

    return ready;
}

/**
 * A ref for the element that says, as data-memory="saved" or "saving", whether every press
 * has reached esback -- for a browser driving the game, which must not reload before it has.
 * Written straight onto the element: a re-render on every save would be a re-render nothing
 * on screen needs.
 */
export function useEsGameMemoryMark( pCampaignId ) {
    const store = useEsGameMemory( pCampaignId );
    const detach = useRef( null );

    return useCallback( ( pElement ) => {
        detach.current?.();
        detach.current = null;
        if ( !pElement ) return;
        const mark = () => { pElement.dataset.memory = !store || store.isSaved() ? 'saved' : 'saving'; };
        mark();
        if ( store ) detach.current = store.subscribe( mark );
    }, [ store ] );
}

// The nav's Session this page load has closed: { account, campaignId, key, at }, at being when
// esback had the close (Infinity until then). esback is the truth -- it skips a closed session
// in campaignMemory/session -- and this only keeps the entry hidden from answers asked before
// esback knew, and from the game page's own word (esSessionGame).
let closed = null;
const closedListeners = new Set();

const setClosed = ( pClosed ) => {
    closed = pClosed;
    closedListeners.forEach( ( pListener ) => pListener() );
};

export const subscribeClosedSession = ( pListener ) => {
    closedListeners.add( pListener );
    return () => closedListeners.delete( pListener );
};

export const readClosedSession = () => closed;

/** A new page load, as far as a closed Session is concerned: for the tests. */
export const esForgetClosedSession = () => { closed = null; };

/**
 * The account closed the nav's Session for this game (Tim, 2026-10-22: "just close the nav
 * item"). The entry goes at once; esback keeps it closed for that session on every device and
 * answers the table Session falls through to, which this resolves with as { game } (game null
 * for none), or null when esback was not told. The session itself runs on. pGame is
 * { key, campaignId }; the game page's word has no campaign id, so esback's session answer
 * supplies it when it names the same table.
 */
export function esCloseSession( pAccountId, pGame ) {
    setClosed( { account: pAccountId, campaignId: pGame.campaignId ?? null, key: pGame.key, at: Infinity } );
    const aFind = pGame.campaignId != null ? Promise.resolve( pGame.campaignId )
        : esRead( API_CAMPAIGN_MEMORY_SESSION ).then( ( pNow ) => ( pNow?.key === pGame.key ? pNow.campaignId : null ) );
    return aFind
        .then( ( pCampaignId ) => {
            if ( pCampaignId == null ) return null;
            if ( closed?.key === pGame.key ) setClosed( { ...closed, campaignId: pCampaignId } );
            return esPost( API_CAMPAIGN_MEMORY + '/' + pCampaignId + '/close' )
                .then( ( pNext ) => ( { game: pNext?.key ? pNext : null } ) );
        } )
        .catch( () => null )
        .finally( () => {
            if ( closed?.key === pGame.key && closed.at === Infinity ) setClosed( { ...closed, at: Date.now() } );
        } );
}

/**
 * The account opened this game; the newest one is where signing in lands. Opening it brings
 * back a Session closed on the nav, here at once and in esback with the visit.
 */
export function esVisitGame( pCampaignId ) {
    if ( closed?.campaignId === pCampaignId ) setClosed( null );
    return esPost( API_CAMPAIGN_MEMORY + '/' + pCampaignId + '/visit' ).catch( () => {} );
}

// What the game holds for the length of the page load, per account per campaign per name.
const kept = new Map();

/** A new page load, as far as useEsKept is concerned: for the tests, which share one module. */
export const esForgetKept = () => kept.clear();

/**
 * useState that outlives the game page within one page load: an unsaved draft -- the Notes
 * form, the chat composer, the console's chat box -- and the shell's passing picks, which a
 * trip to Codex or Campaigns and back through the nav's Session would otherwise drop with the
 * unmount. Not esback's: a draft runs past the 512 characters a remembered value may hold,
 * and words typed and not saved are this browser's alone. Per account, so one browser's
 * accounts never read each other's; a reload starts empty. A null campaign is plain state.
 */
export function useEsKept( pCampaignId, pName, pInitial ) {
    const aAccountId = useAuth()?.account?.accountId ?? null;
    const aKey = pCampaignId == null ? null : aAccountId + '|' + pCampaignId + '|' + pName;
    const [ value, setValue ] = useState( () => ( aKey !== null && kept.has( aKey ) ? kept.get( aKey ) : pInitial ) );
    const current = useRef( value );
    current.current = value;
    const initial = useRef( pInitial );

    // The campaign or the account arriving after the first render.
    const at = useRef( aKey );
    useEffect( () => {
        if ( at.current === aKey ) return;
        at.current = aKey;
        const aValue = aKey !== null && kept.has( aKey ) ? kept.get( aKey ) : initial.current;
        current.current = aValue;
        setValue( aValue );
    }, [ aKey ] );

    const keep = useCallback( ( pNext ) => {
        const aValue = typeof pNext === 'function' ? pNext( current.current ) : pNext;
        current.current = aValue;
        setValue( aValue );
        if ( aKey === null ) return;
        if ( aValue === null || aValue === undefined ) kept.delete( aKey );
        else kept.set( aKey, aValue );
    }, [ aKey ] );

    return [ value, keep ];
}
